/**
 * Правки markdown-форматирования текстового поля: по значению и выделению возвращают
 * заменяемый диапазон, новый текст и выделение после замены. Повторное применение снимает формат.
 */

export type MarkdownFormatAction = "bold" | "italic" | "strike" | "heading" | "bullets" | "numbers" | "link"

export interface MarkdownEdit {
  start: number
  end: number
  text: string
  selStart: number
  selEnd: number
}

const INLINE_MARKERS: Record<"bold" | "italic" | "strike", string> = { bold: "**", italic: "_", strike: "~~" }

function toggleInline(value: string, s: number, e: number, marker: string): MarkdownEdit {
  const m = marker.length
  if (value.slice(s - m, s) === marker && value.slice(e, e + m) === marker) {
    return { start: s - m, end: e + m, text: value.slice(s, e), selStart: s - m, selEnd: e - m }
  }
  const sel = value.slice(s, e)
  if (sel.length >= 2 * m && sel.startsWith(marker) && sel.endsWith(marker)) {
    const inner = sel.slice(m, sel.length - m)
    return { start: s, end: e, text: inner, selStart: s, selEnd: s + inner.length }
  }
  // Пробелы по краям выделения оставляем снаружи: «** текст **» markdown не распознаёт
  const lead = sel.match(/^\s*/)![0].length
  const trail = sel.length > lead ? sel.match(/\s*$/)![0].length : 0
  const core = sel.slice(lead, sel.length - trail)
  const text = sel.slice(0, lead) + marker + core + marker + sel.slice(sel.length - trail)
  const innerStart = s + lead + m
  return { start: s, end: e, text, selStart: innerStart, selEnd: innerStart + core.length }
}

const LINE_PREFIX: Record<"heading" | "bullets" | "numbers", RegExp> = {
  heading: /^#{1,6}\s+/,
  bullets: /^[-*+]\s+/,
  numbers: /^\d+[.)]\s+/,
}

function toggleLinePrefix(value: string, s: number, e: number, kind: "heading" | "bullets" | "numbers"): MarkdownEdit {
  const start = value.lastIndexOf("\n", s - 1) + 1
  // Выделение, заканчивающееся переводом строки, не захватывает следующую строку
  const endNl = value.indexOf("\n", e > s && value[e - 1] === "\n" ? e - 1 : e)
  const end = endNl === -1 ? value.length : endNl
  const lines = value.slice(start, end).split("\n")
  const re = LINE_PREFIX[kind]
  const content = lines.filter((l) => l.trim())
  const allHave = content.length > 0 && content.every((l) => re.test(l))
  let n = 0
  const text = lines.map((l) => {
    if (allHave) return l.replace(re, "")
    if (!l.trim() && lines.length > 1) return l
    const bare = l.replace(LINE_PREFIX.heading, "").replace(LINE_PREFIX.bullets, "").replace(LINE_PREFIX.numbers, "")
    n += 1
    return (kind === "heading" ? "### " : kind === "bullets" ? "- " : `${n}. `) + bare
  }).join("\n")
  const caret = start + text.length
  return { start, end, text, selStart: s === e ? caret : start, selEnd: caret }
}

function insertLink(value: string, s: number, e: number): MarkdownEdit {
  const sel = value.slice(s, e)
  if (/^https?:\/\/\S+$/.test(sel.trim())) {
    return { start: s, end: e, text: `[](${sel.trim()})`, selStart: s + 1, selEnd: s + 1 }
  }
  const label = sel || "текст"
  const urlStart = s + label.length + 3
  return { start: s, end: e, text: `[${label}](https://)`, selStart: urlStart, selEnd: urlStart + "https://".length }
}

export function computeMarkdownEdit(value: string, selStart: number, selEnd: number, action: MarkdownFormatAction): MarkdownEdit {
  const s = Math.min(selStart, selEnd)
  const e = Math.max(selStart, selEnd)
  if (action === "bold" || action === "italic" || action === "strike") return toggleInline(value, s, e, INLINE_MARKERS[action])
  if (action === "link") return insertLink(value, s, e)
  return toggleLinePrefix(value, s, e, action)
}
